import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BlockIcon from '@mui/icons-material/Block';
import SettingsIcon from '@mui/icons-material/Settings';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import BoltIcon from '@mui/icons-material/Bolt';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import dayjs from 'dayjs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import paths from '@src/paths';
import FacebookCardsStaffPanel from '@src/components/cardVault/FacebookCardsStaffPanel';
import { useMessage } from '@src/context/common';
import apiClient from '@src/api/clients';
import {
  bulkGenerateInvoices,
  bulkSendInvoices,
  bulkVoidInvoices,
  deleteInvoice,
  downloadInvoicePdf,
  autoChargeInvoice,
  getLatePaymentStandings,
  listBulkGenerationClients,
  markInvoicePaid,
  listInvoices,
  listPeriodSummary,
  retrieveInvoice,
  sendInvoice,
  voidInvoice,
} from '@src/api/invoices';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'outstanding', label: 'Outstanding / Overdue' },
  { value: 'void', label: 'Void' },
  { value: 'paid', label: 'Paid' },
];

const PAYMENT_METHOD_FILTER_OPTIONS = [
  { value: 'all', label: 'All methods' },
  { value: 'card', label: 'Card' },
  { value: 'us_bank_account', label: 'ACH' },
];

const PAYMENT_STATE_FILTER_OPTIONS = [
  { value: 'all', label: 'All payment states' },
  { value: 'paid', label: 'Paid' },
  { value: 'processing', label: 'Processing' },
  { value: 'failed', label: 'Failed' },
  { value: 'none', label: 'None' },
];

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORT = { field: 'issue_date', direction: 'desc' };
const PERIOD_WINDOW_OPTIONS = [
  { value: '3', label: 'Last 3 periods' },
  { value: '6', label: 'Last 6 periods' },
  { value: '12', label: 'Last 12 periods' },
  { value: 'all', label: 'All periods' },
];

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return String(value);
  }
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const formatSpendFundingLabel = (value) => {
  if (!value) return '—';
  if (value === 'client_funds_spend') return 'Client funded';
  if (value === 'guru_funds_spend') return 'Lead Gurus funded';
  return value;
};

const formatPaymentSourceLabel = (value) => {
  if (!value) return '—';
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'stripe') return 'Stripe';
  if (normalized === 'manual') return 'Manual';
  return normalized;
};

const formatPaymentMethodLabel = (value) => {
  if (!value) return '—';
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'ach_wire') return 'ACH/Wire';
  if (normalized === 'us_bank_account') return 'ACH';
  if (normalized === 'check') return 'Check';
  if (normalized === 'card') return 'Card';
  return normalized.replace(/_/g, ' ');
};

const getPaymentState = (invoice) => {
  const paymentState = String(invoice?.payment_state || '').toLowerCase();
  if (!paymentState) {
    if (String(invoice?.status || '').toLowerCase() === 'paid') return { label: 'Paid', color: 'success' };
    if (invoice?.payment_failed_at) return { label: 'Failed', color: 'error' };
    if (String(invoice?.payment_source || '').toLowerCase() === 'stripe') return { label: 'Processing', color: 'info' };
  }
  if (paymentState === 'paid') return { label: 'Paid', color: 'success' };
  if (paymentState === 'failed') return { label: 'Failed', color: 'error' };
  if (paymentState === 'processing') return { label: 'Processing', color: 'info' };
  return { label: '—', color: 'default' };
};

const statusColor = {
  draft: 'default',
  sent: 'info',
  outstanding: 'warning',
  void: 'default',
  paid: 'success',
};

const resolveStatusColor = (status) => statusColor[status] || 'default';

// Grouped-client territory billing: bulk generation invoices exactly the
// clients picked, so a branch that was not picked (or is inactive, or is
// mis-flagged on funding) keeps its territory spend billed to it and unseen.
const TERRITORY_WARNING_LABELS = {
  branch_not_selected: 'Branch not selected — its territory spend was not invoiced',
  inactive_branch: 'Inactive branch still carrying territory spend',
  funding_mismatch: 'Branch funding flag differs from its grouped client',
};

const getStatusPresentation = (invoice) => {
  const status = String(invoice?.status || '').toLowerCase();
  const dueDate = invoice?.due_date;
  const overdue =
    status === 'outstanding' &&
    dueDate &&
    dayjs(dueDate).isValid() &&
    dayjs(dueDate).isBefore(dayjs().startOf('day'));
  if (overdue) {
    const daysOverdue = dayjs().startOf('day').diff(dayjs(dueDate).startOf('day'), 'day');
    return { label: `OVERDUE ${daysOverdue}d`, color: 'error' };
  }
  return { label: status ? status.toUpperCase() : 'UNKNOWN', color: resolveStatusColor(status) };
};

function LatePaymentStandingsPanel() {
  const [standings, setStandings] = useState(null);

  useEffect(() => {
    getLatePaymentStandings()
      .then((res) => setStandings(res?.data || null))
      .catch(() => setStandings(null));
  }, []);

  const results = standings?.results || [];
  if (!results.length) return null;
  const limit = standings.strike_limit || 3;
  const atLimit = results.filter((r) => r.at_limit);

  return (
    <Box mt={3}>
      <Card>
        <CardHeader
          title="Late-payment standings"
          subheader={`Clients with invoices that hit the day-6 pause in the last ${Math.round((standings.window_days || 365) / 30)} months. At ${limit} strikes, it's time for a conversation about moving to client-funded ad spend — new contract first, then switch billing before relaunch. Nothing switches automatically.`}
        />
        <CardContent sx={{ pt: 0 }}>
          {atLimit.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {atLimit.length === 1
                  ? `${atLimit[0].name} has reached ${limit} late payments — time to reach out about moving to client-funded billing.`
                  : `${atLimit.length} clients have reached ${limit} late payments — time to reach out about moving to client-funded billing.`}
              </Typography>
            </Alert>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {results.map((entity) => (
              <Tooltip
                key={`${entity.entity_type}-${entity.entity_id}`}
                title={(entity.strikes || [])
                  .map((s) => `${s.invoice_number} — ${dayjs(s.struck_at).format('MMM D, YYYY')}`)
                  .join(' · ')}
              >
                <Chip
                  label={`${entity.name}: ${entity.strike_count} of ${limit}`}
                  color={entity.at_limit ? 'error' : entity.strike_count >= limit - 1 ? 'warning' : 'default'}
                  variant={entity.at_limit ? 'filled' : 'outlined'}
                  size="small"
                />
              </Tooltip>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

function mapInvoicesResponse(data) {
  if (Array.isArray(data)) return { results: data, count: data.length };
  if (data && Array.isArray(data.results)) return { results: data.results, count: data.count ?? data.results.length };
  return { results: [], count: 0 };
}

const toNumber = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const getPeriodKey = (invoice) =>
  `${invoice?.period_start || 'unknown'}__${invoice?.period_end || 'unknown'}`;

const getPeriodDisplayLabel = (periodStart, periodEnd) => {
  const start = dayjs(periodStart);
  const end = dayjs(periodEnd);
  if (!start.isValid() || !end.isValid()) return `${periodStart || '—'} – ${periodEnd || '—'}`;
  const sameMonth = start.year() === end.year() && start.month() === end.month();
  const monthLabel = start.format('MMM YYYY');
  const endOfMonth = start.endOf('month').date();
  // P1 = 1st–15th (semi-monthly first half)
  if (sameMonth && start.date() === 1 && end.date() === 15) return `${monthLabel} P1`;
  // P2 = 16th–end (semi-monthly second half)
  if (sameMonth && start.date() === 16 && end.date() === endOfMonth) return `${monthLabel} P2`;
  // Full month = just the month label (monthly clients)
  if (sameMonth && start.date() === 1 && end.date() === endOfMonth) return monthLabel;
  // Fallback for custom ranges
  if (start.year() === end.year()) return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
  return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
};

const AutopayBadge = ({ consent }) => {
  if (!consent || !consent.authorized) {
    return null;
  }
  const methods = Array.isArray(consent.methods) ? consent.methods : [];
  const hasCard = methods.includes('card');
  const hasBank = methods.includes('us_bank_account');
  if (!hasCard && !hasBank) {
    return null;
  }
  let label;
  if (hasCard && hasBank) {
    label = 'Auto-charge authorized: Card + Bank';
  } else if (hasCard) {
    label = 'Auto-charge authorized: Card';
  } else {
    label = 'Auto-charge authorized: Bank (ACH)';
  }
  return (
    <Tooltip title={label} arrow>
      <Stack
        component="span"
        direction="row"
        spacing={0.25}
        alignItems="center"
        sx={{
          color: 'success.dark',
          bgcolor: 'rgba(var(--mui-palette-success-mainChannel) / 0.12)',
          border: '1px solid',
          borderColor: 'success.light',
          borderRadius: 1,
          px: 0.5,
          py: 0.125,
          ml: 0.5,
          lineHeight: 1,
        }}
        aria-label={label}
      >
        <BoltIcon sx={{ fontSize: 14 }} />
        {hasCard && <CreditCardIcon sx={{ fontSize: 14 }} />}
        {hasBank && <AccountBalanceIcon sx={{ fontSize: 14 }} />}
      </Stack>
    </Tooltip>
  );
};

const compareInvoiceValues = (left, right, { field, direction }) => {
  const multiplier = direction === 'desc' ? -1 : 1;
  if (field === 'client_sort_name') {
    const leftValue = String(left?.client_name || left?.client || '').toLowerCase();
    const rightValue = String(right?.client_name || right?.client || '').toLowerCase();
    return leftValue.localeCompare(rightValue) * multiplier;
  }
  if (field === 'total_amount') return (toNumber(left?.total_amount) - toNumber(right?.total_amount)) * multiplier;
  if (field === 'issue_date') {
    const leftValue = dayjs(left?.issue_date).valueOf();
    const rightValue = dayjs(right?.issue_date).valueOf();
    if (leftValue !== rightValue) return (leftValue - rightValue) * multiplier;
  }
  return (Number(left?.id || 0) - Number(right?.id || 0)) * multiplier;
};

const toCsvCell = (value) => {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

// --- Row action overflow menu ---
function InvoiceActionMenu({
  invoice,
  onDownloadPdf,
  onVoid,
  onDelete,
  downloadingId,
  voidingId,
  deletingId,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const isPaid = invoice.status === 'paid';
  const isVoid = invoice.status === 'void';
  const isDraft = invoice.status === 'draft';
  const isProcessing = getPaymentState(invoice).label === 'Processing';

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem
          component={RouterLink}
          to={paths.dashboard.invoices.edit(invoice.id)}
          disabled={isPaid || isVoid}
          onClick={() => setAnchorEl(null)}
        >
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => { onDownloadPdf(invoice); setAnchorEl(null); }}
          disabled={downloadingId === invoice.id}
        >
          <ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => { onVoid(invoice); setAnchorEl(null); }}
          disabled={isPaid || isVoid || isProcessing || voidingId === invoice.id}
        >
          <ListItemIcon><BlockIcon fontSize="small" color="warning" /></ListItemIcon>
          <ListItemText>Void</ListItemText>
        </MenuItem>
        {isDraft && (
          <MenuItem
            onClick={() => { onDelete(invoice); setAnchorEl(null); }}
            disabled={deletingId === invoice.id}
          >
            <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

const canSendInvoice = (invoice) => {
  if (!invoice) return false;
  const status = String(invoice.status || '').toLowerCase();
  if (status === 'paid' || status === 'void') return false;
  if (getPaymentState(invoice).label === 'Processing') return false;
  return true;
};

function QuickSendDialog({ open, invoice, onClose, onSuccess }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [sendError, setSendError] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !invoice?.id) return undefined;
    let cancelled = false;
    setDetail(null);
    setLoadError('');
    setSendError('');
    setMessage('');
    setLoading(true);
    retrieveInvoice(invoice.id)
      .then((res) => {
        if (!cancelled) setDetail(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load invoice detail', err);
        setLoadError('Unable to load invoice details.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, invoice?.id]);

  const handleSend = () => {
    if (!invoice?.id) return;
    setSendError('');
    setSending(true);
    const payload = {};
    if (message.trim()) payload.message = message.trim();
    if (detail?.needs_amount_confirmation) payload.confirm_amount = true;
    sendInvoice(invoice.id, payload)
      .then((res) => {
        onSuccess(res.data);
      })
      .catch((err) => {
        console.error('Failed to send invoice', err);
        const detailMessage =
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          (typeof err?.response?.data === 'string' ? err.response.data : '');
        setSendError(detailMessage || 'Failed to send the invoice. Please review and try again.');
      })
      .finally(() => setSending(false));
  };

  const lineItems = detail?.line_items || [];
  const billingContacts = Array.isArray(detail?.billing_contacts_snapshot)
    ? detail.billing_contacts_snapshot
    : [];
  const recipients = billingContacts
    .map((contact) => {
      if (!contact) return '';
      if (typeof contact === 'string') return contact;
      return contact.email || contact.name || '';
    })
    .filter(Boolean);

  const needsApproval = Boolean(detail?.needs_amount_confirmation);
  const sendDisabled =
    sending ||
    loading ||
    !detail ||
    !lineItems.length ||
    !canSendInvoice(detail);

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Send invoice
        {invoice?.number ? ` ${invoice.number}` : invoice?.id ? ` #${invoice.id}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : detail ? (
          <Stack spacing={2}>
            <Alert severity="info">
              Review the amount and line items below before sending. The client will receive an emailed copy with a Stripe payment link.
            </Alert>

            <Paper variant="outlined">
              <Box p={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} flexWrap="wrap" useFlexGap>
                  <Stack spacing={0.25}>
                    <Typography variant="overline" color="text.secondary">Client</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {detail.client_name || detail.billing_client_name || detail.parent_company_name || '—'}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.25}>
                    <Typography variant="overline" color="text.secondary">Period</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {getPeriodDisplayLabel(detail.period_start, detail.period_end)}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.25}>
                    <Typography variant="overline" color="text.secondary">Due date</Typography>
                    <Typography variant="body2" fontWeight={600}>{detail.due_date || '—'}</Typography>
                  </Stack>
                  <Stack spacing={0.25}>
                    <Typography variant="overline" color="text.secondary">Status</Typography>
                    <Chip
                      size="small"
                      label={getStatusPresentation(detail).label}
                      color={getStatusPresentation(detail).color}
                    />
                  </Stack>
                </Stack>
              </Box>
            </Paper>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Line items</Typography>
              {lineItems.length ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Unit</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineItems.map((item, idx) => (
                        <TableRow key={item.id || `line-${idx}`}>
                          <TableCell>{item.description || '—'}</TableCell>
                          <TableCell align="right">{item.quantity ?? '—'}</TableCell>
                          <TableCell align="right">{formatCurrency(item.unit_amount)}</TableCell>
                          <TableCell align="right">{formatCurrency(item.line_total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="warning">This invoice has no line items — it cannot be sent.</Alert>
              )}
            </Box>

            <Paper variant="outlined">
              <Box p={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700}>Total</Typography>
                  <Typography variant="h6">{formatCurrency(detail.total_amount)}</Typography>
                </Stack>
              </Box>
            </Paper>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Recipients</Typography>
              {recipients.length ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {recipients.map((value) => (
                    <Chip key={value} size="small" label={value} variant="outlined" />
                  ))}
                </Stack>
              ) : (
                <Alert severity="warning">No billing contacts on file. Add contacts on the client before sending.</Alert>
              )}
            </Box>

            <TextField
              label="Message (optional)"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              multiline
              minRows={3}
              placeholder="Add an optional note the recipient will see with the invoice"
              fullWidth
              disabled={sending}
            />

            {needsApproval && (
              <Alert severity="warning">
                This invoice has not been approved yet. Clicking <strong>Approve &amp; send</strong> below will approve the amount and send it in one step.
              </Alert>
            )}
            {sendError && <Alert severity="error">{sendError}</Alert>}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        <LoadingButton
          variant="contained"
          color={needsApproval ? 'warning' : 'primary'}
          startIcon={<SendIcon />}
          loading={sending}
          disabled={sendDisabled}
          onClick={handleSend}
        >
          {needsApproval ? 'Approve & send' : 'Send invoice'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

// URL param keys mapping
const FILTER_PARAM_KEYS = {
  search: 'q',
  amount: 'amount',
  status: 'status',
  funding: 'funding',
  approval: 'approval',
  paymentMethod: 'pm',
  paymentState: 'ps',
  paymentDateStart: 'pds',
  paymentDateEnd: 'pde',
  issueStart: 'is',
  issueEnd: 'ie',
};

const FILTER_DEFAULTS = {
  search: '',
  amount: '',
  status: 'all',
  funding: 'all',
  approval: 'all',
  paymentMethod: 'all',
  paymentState: 'all',
  paymentDateStart: '',
  paymentDateEnd: '',
  issueStart: '',
  issueEnd: '',
};

const readFiltersFromParams = (searchParams) => {
  const filters = { ...FILTER_DEFAULTS };
  Object.entries(FILTER_PARAM_KEYS).forEach(([key, param]) => {
    const value = searchParams.get(param);
    if (value !== null && value !== '') filters[key] = value;
  });
  return filters;
};

const readPaginationFromParams = (searchParams) => ({
  page: Math.max(0, parseInt(searchParams.get('page') || '0', 10) || 0),
  rowsPerPage: parseInt(searchParams.get('pageSize') || String(DEFAULT_PAGE_SIZE), 10) || DEFAULT_PAGE_SIZE,
});

const readSortFromParams = (searchParams) => {
  const field = searchParams.get('sortBy') || DEFAULT_SORT.field;
  const direction = searchParams.get('sortDir') || DEFAULT_SORT.direction;
  return { field, direction };
};

export default function InvoiceListPage() {
  const message = useMessage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => readFiltersFromParams(searchParams));
  const [showMoreFilters, setShowMoreFilters] = useState(() => {
    // Auto-open if any advanced filter is set from URL
    const f = readFiltersFromParams(searchParams);
    return Object.entries(FILTER_DEFAULTS).some(
      ([key, def]) => !['search', 'status'].includes(key) && f[key] !== def
    );
  });
  const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get(FILTER_PARAM_KEYS.search) || '');
  const [debouncedAmount, setDebouncedAmount] = useState(() => searchParams.get(FILTER_PARAM_KEYS.amount) || '');
  const searchTimerRef = useRef(null);
  const amountTimerRef = useRef(null);
  const fetchAbortRef = useRef(null);
  const [pagination, setPagination] = useState(() => readPaginationFromParams(searchParams));
  const [sort, setSort] = useState(() => readSortFromParams(searchParams));
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [quickSendTarget, setQuickSendTarget] = useState(null);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [voidingId, setVoidingId] = useState(null);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');

  const [clients, setClients] = useState([]);

  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkClients, setBulkClients] = useState([]);
  const [bulkClientsLoading, setBulkClientsLoading] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [bulkFundingFilter, setBulkFundingFilter] = useState('all');
  const [bulkPeriod, setBulkPeriod] = useState({ start: '', end: '' });
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkSummary, setBulkSummary] = useState(null);
  const [bulkError, setBulkError] = useState('');
  const [periodSummaries, setPeriodSummaries] = useState([]);
  const [periodSummaryLoading, setPeriodSummaryLoading] = useState(false);
  const [periodWindow, setPeriodWindow] = useState('6');
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [collapsedPeriods, setCollapsedPeriods] = useState({});
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkVoiding, setBulkVoiding] = useState(false);
  const [bulkVoidDialogOpen, setBulkVoidDialogOpen] = useState(false);
  const [bulkVoidReason, setBulkVoidReason] = useState('');

  // Sync state → URL params (debounced to avoid excessive history entries)
  const urlSyncRef = useRef(null);
  useEffect(() => {
    clearTimeout(urlSyncRef.current);
    urlSyncRef.current = setTimeout(() => {
      const params = new URLSearchParams();
      Object.entries(FILTER_PARAM_KEYS).forEach(([key, param]) => {
        const value = key === 'search' ? debouncedSearch : key === 'amount' ? debouncedAmount : filters[key];
        if (value && value !== FILTER_DEFAULTS[key]) params.set(param, value);
      });
      if (pagination.page > 0) params.set('page', String(pagination.page));
      if (pagination.rowsPerPage !== DEFAULT_PAGE_SIZE) params.set('pageSize', String(pagination.rowsPerPage));
      if (sort.field !== DEFAULT_SORT.field) params.set('sortBy', sort.field);
      if (sort.direction !== DEFAULT_SORT.direction) params.set('sortDir', sort.direction);
      setSearchParams(params, { replace: true });
    }, 300);
    return () => clearTimeout(urlSyncRef.current);
  }, [debouncedSearch, debouncedAmount, filters, pagination, sort, setSearchParams]);

  // Track how many advanced filters are active
  const activeAdvancedFilterCount = useMemo(() => {
    let count = 0;
    if (filters.funding !== 'all') count++;
    if (filters.approval !== 'all') count++;
    if (filters.paymentMethod !== 'all') count++;
    if (filters.paymentState !== 'all') count++;
    if (filters.paymentDateStart) count++;
    if (filters.paymentDateEnd) count++;
    if (filters.issueStart) count++;
    if (filters.issueEnd) count++;
    if (filters.amount) count++;
    return count;
  }, [filters]);

  const buildFilterQueryParams = useCallback(() => {
    const amountInput = String(debouncedAmount ?? '').trim();
    const amount = Number(amountInput);
    const tolerance = 3;
    const hasAmount = amountInput !== '' && Number.isFinite(amount);
    const hasTolerance = Number.isFinite(tolerance) && tolerance > 0;
    return {
      search: debouncedSearch,
      total_amount: hasAmount && !hasTolerance ? amount : undefined,
      total_amount__gte: hasAmount && hasTolerance ? amount - tolerance : undefined,
      total_amount__lte: hasAmount && hasTolerance ? amount + tolerance : undefined,
      status: filters.status !== 'all' ? filters.status : undefined,
      spend_funding: filters.funding !== 'all' ? filters.funding : undefined,
      needs_amount_confirmation:
        filters.approval === 'all' ? undefined : filters.approval === 'approved' ? false : true,
      payment_method: filters.paymentMethod !== 'all' ? filters.paymentMethod : undefined,
      payment_state: filters.paymentState !== 'all' ? filters.paymentState : undefined,
      payment_date__gte: filters.paymentDateStart || undefined,
      payment_date__lte: filters.paymentDateEnd || undefined,
      issue_date__gte: filters.issueStart || undefined,
      issue_date__lte: filters.issueEnd || undefined,
    };
  }, [debouncedSearch, debouncedAmount, filters.status, filters.funding, filters.approval, filters.paymentMethod, filters.paymentState, filters.paymentDateStart, filters.paymentDateEnd, filters.issueStart, filters.issueEnd]);

  const buildInvoiceQueryParams = useCallback(
    ({ page, pageSize }) => ({
      page,
      page_size: pageSize,
      ...buildFilterQueryParams(),
      ordering: `${sort.direction === 'desc' ? '-' : ''}${sort.field}`,
    }),
    [buildFilterQueryParams, sort]
  );

  const clientFundingById = useMemo(() => {
    const map = new Map();
    clients.forEach((client) => {
      if (client?.id) map.set(client.id, client.spendFunding || '');
    });
    return map;
  }, [clients]);

  const filteredClients = useMemo(() => bulkClients, [bulkClients]);

  const loadClients = useCallback(() => {
    apiClient
      .request({ url: 'dashboard/clients/', method: 'GET' })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        setClients(
          data
            .map((client) => ({ id: client.id, name: client.name, spendFunding: client.spend_funding }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      })
      .catch((err) => {
        console.error('Failed to load clients', err);
        message.error('Unable to load clients for bulk generation.');
      });
  }, [message]);

  useEffect(() => { loadClients(); }, [loadClients]);

  useEffect(() => {
    if (!bulkDialogOpen) return;
    if ((bulkPeriod.start && !bulkPeriod.end) || (!bulkPeriod.start && bulkPeriod.end)) {
      setBulkClientsLoading(false);
      setBulkClients([]);
      return;
    }
    setBulkError('');
    setBulkClientsLoading(true);
    listBulkGenerationClients({
      period_start: bulkPeriod.start || undefined,
      period_end: bulkPeriod.end || undefined,
      spend_funding: bulkFundingFilter !== 'all' ? bulkFundingFilter : undefined,
    })
      .then((res) => {
        const data = Array.isArray(res.data?.results) ? res.data.results : [];
        setBulkClients(
          data.map((client) => ({
            id: client.id,
            name: client.name,
            spendFunding: client.spend_funding,
            billingCycle: client.billing_cycle || 'monthly',
            periodStart: client.period_start,
            periodEnd: client.period_end,
            periodSpend: client.period_spend,
          }))
        );
      })
      .catch((err) => {
        console.error('Failed to load spend-eligible clients', err);
        setBulkClients([]);
        setBulkError('Unable to load clients with spend for the selected period.');
      })
      .finally(() => setBulkClientsLoading(false));
  }, [bulkDialogOpen, bulkFundingFilter, bulkPeriod.end, bulkPeriod.start]);

  useEffect(() => {
    const validIds = new Set(filteredClients.map((client) => client.id));
    setSelectedClientIds((prev) => prev.filter((id) => validIds.has(Number(id))));
  }, [filteredClients]);

  const fetchInvoices = useCallback(() => {
    if (fetchAbortRef.current) fetchAbortRef.current.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    setLoading(true);
    setError('');
    const params = buildInvoiceQueryParams({ page: pagination.page + 1, pageSize: pagination.rowsPerPage });
    listInvoices(params, { signal: controller.signal })
      .then((res) => {
        const mapped = mapInvoicesResponse(res.data);
        setInvoices(mapped.results);
        setTotalCount(mapped.count);
      })
      .catch((err) => {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        console.error('Failed to fetch invoices', err);
        setError('Failed to fetch invoices. Please try again.');
      })
      .finally(() => setLoading(false));
  }, [buildInvoiceQueryParams, pagination.page, pagination.rowsPerPage]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // Fetch period summaries from dedicated backend endpoint (replaces client-side analytics)
  const fetchPeriodSummary = useCallback(() => {
    setPeriodSummaryLoading(true);
    listPeriodSummary(buildFilterQueryParams())
      .then((res) => {
        const data = Array.isArray(res.data?.results) ? res.data.results : [];
        setPeriodSummaries(
          data.map((row) => ({
            period_start: row.period_start,
            period_end: row.period_end,
            total_billed: toNumber(row.total_billed),
            total_paid: toNumber(row.total_paid),
            total_outstanding: toNumber(row.total_outstanding),
            total_overdue: toNumber(row.total_overdue),
            total_sent: toNumber(row.total_sent),
            total_draft: toNumber(row.total_draft),
            paid_revenue: toNumber(row.paid_revenue),
            outstanding_revenue: toNumber(row.outstanding_revenue),
            overdue_revenue: toNumber(row.overdue_revenue),
            invoice_count: row.invoice_count || 0,
          }))
        );
      })
      .catch((err) => {
        console.error('Failed to fetch period summary', err);
        setPeriodSummaries([]);
      })
      .finally(() => setPeriodSummaryLoading(false));
  }, [buildFilterQueryParams]);

  useEffect(() => { fetchPeriodSummary(); }, [fetchPeriodSummary]);

  useEffect(() => {
    if (!invoices.length) {
      setSelectedInvoiceIds([]);
      return;
    }
    const validIds = new Set(invoices.map((invoice) => String(invoice.id)));
    setSelectedInvoiceIds((prev) => prev.filter((id) => validIds.has(String(id))));
  }, [invoices]);

  const periodSummaryByKey = useMemo(() => {
    const map = new Map();
    periodSummaries.forEach((summary) => {
      map.set(`${summary.period_start || 'unknown'}__${summary.period_end || 'unknown'}`, summary);
    });
    return map;
  }, [periodSummaries]);

  const selectedInvoiceIdSet = useMemo(
    () => new Set(selectedInvoiceIds.map((id) => String(id))),
    [selectedInvoiceIds]
  );

  const allVisibleSelected =
    invoices.length > 0 && invoices.every((invoice) => selectedInvoiceIdSet.has(String(invoice.id)));
  const someVisibleSelected =
    invoices.some((invoice) => selectedInvoiceIdSet.has(String(invoice.id))) && !allVisibleSelected;

  const filteredPeriodSummaries = useMemo(() => {
    if (periodWindow === 'all') return periodSummaries;
    const count = Number(periodWindow);
    if (!Number.isFinite(count) || count <= 0) return periodSummaries;
    return periodSummaries.slice(-count);
  }, [periodSummaries, periodWindow]);

  const invoicePeriodGroups = useMemo(() => {
    const groups = [];
    const byKey = new Map();
    invoices.forEach((invoice) => {
      const key = getPeriodKey(invoice);
      if (!byKey.has(key)) {
        const group = { key, periodStart: invoice.period_start, periodEnd: invoice.period_end, invoices: [] };
        byKey.set(key, group);
        groups.push(group);
      }
      byKey.get(key).invoices.push(invoice);
    });
    groups.sort((a, b) => {
      const left = dayjs(a.periodStart);
      const right = dayjs(b.periodStart);
      return (right.isValid() ? right.valueOf() : 0) - (left.isValid() ? left.valueOf() : 0);
    });
    groups.forEach((group) => { group.invoices.sort((a, b) => compareInvoiceValues(a, b, sort)); });
    return groups;
  }, [invoices, sort]);

  useEffect(() => {
    if (!invoicePeriodGroups.length) return;
    setCollapsedPeriods((prev) => {
      const next = { ...prev };
      invoicePeriodGroups.forEach((group) => {
        if (next[group.key] === undefined) next[group.key] = false;
      });
      return next;
    });
  }, [invoicePeriodGroups]);

  const totals = useMemo(
    () =>
      filteredPeriodSummaries.reduce(
        (acc, row) => ({
          total_billed: acc.total_billed + row.total_billed,
          total_paid: acc.total_paid + row.total_paid,
          total_outstanding: acc.total_outstanding + row.total_outstanding,
          total_overdue: acc.total_overdue + row.total_overdue,
        }),
        { total_billed: 0, total_paid: 0, total_outstanding: 0, total_overdue: 0 }
      ),
    [filteredPeriodSummaries]
  );

  const chartData = useMemo(
    () =>
      filteredPeriodSummaries.map((row) => ({
        period: getPeriodDisplayLabel(row.period_start, row.period_end),
        paid: Number(row.total_paid.toFixed(2)),
        outstanding: Number(row.total_outstanding.toFixed(2)),
        overdue: Number(row.total_overdue.toFixed(2)),
        draft: Number(row.total_draft.toFixed(2)),
      })),
    [filteredPeriodSummaries]
  );

  const handleFilterChange = (key) => (event) => {
    const value = event.target.value;
    setFilters((prev) => ({ ...prev, [key]: value }));
    if (key === 'search') {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        setDebouncedSearch(value);
        setPagination((prev) => ({ ...prev, page: 0 }));
      }, 400);
    } else if (key === 'amount') {
      clearTimeout(amountTimerRef.current);
      amountTimerRef.current = setTimeout(() => {
        setDebouncedAmount(value);
        setPagination((prev) => ({ ...prev, page: 0 }));
      }, 400);
    } else {
      setPagination((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event) => {
    setPagination({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });
  };

  const handleRequestSort = (field) => {
    setSort((prev) => {
      if (prev.field === field) return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      return { field, direction: 'asc' };
    });
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleToggleInvoiceSelection = (invoiceId) => {
    const key = String(invoiceId);
    setSelectedInvoiceIds((prev) => {
      if (prev.some((id) => String(id) === key)) return prev.filter((id) => String(id) !== key);
      return [...prev, invoiceId];
    });
  };

  const togglePeriodCollapse = (periodKey) => {
    setCollapsedPeriods((prev) => ({ ...prev, [periodKey]: !prev[periodKey] }));
  };

  const handleToggleSelectVisible = (event) => {
    const shouldSelect = Boolean(event.target.checked);
    const visibleIds = invoices.map((invoice) => invoice.id);
    setSelectedInvoiceIds((prev) => {
      const prevSet = new Set(prev.map((id) => String(id)));
      if (shouldSelect) {
        visibleIds.forEach((id) => prevSet.add(String(id)));
      } else {
        visibleIds.forEach((id) => prevSet.delete(String(id)));
      }
      return Array.from(prevSet);
    });
  };

  const openQuickSend = (invoice) => {
    if (!canSendInvoice(invoice)) return;
    setQuickSendTarget(invoice);
  };

  const closeQuickSend = () => {
    setQuickSendTarget(null);
  };

  const handleQuickSendSuccess = (updatedInvoice) => {
    message.success('Invoice sent successfully. Stripe payment link created.');
    setInvoices((prev) =>
      prev.map((invoice) => (invoice.id === updatedInvoice.id ? { ...invoice, ...updatedInvoice } : invoice)),
    );
    setQuickSendTarget(null);
  };

  const handleDownloadPdf = useCallback(
    (invoice) => {
      if (!invoice?.id) return;
      setDownloadingId(invoice.id);
      downloadInvoicePdf(invoice.id)
        .then((blob) => {
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `${invoice.number || `invoice-${invoice.id}`}.pdf`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch((err) => {
          console.error('Failed to download invoice PDF', err);
          message.error('Unable to download invoice PDF.');
        })
        .finally(() => setDownloadingId(null));
    },
    [message]
  );

  const openDeleteDialog = (invoice) => {
    if (!invoice || invoice.status !== 'draft') return;
    setDeleteTarget(invoice);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deletingId) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const openVoidDialog = (invoice) => {
    if (!invoice || ['paid', 'void'].includes(invoice.status) || getPaymentState(invoice).label === 'Processing') return;
    setVoidTarget(invoice);
    setVoidReason('');
    setVoidDialogOpen(true);
  };

  const closeVoidDialog = () => {
    if (voidingId) return;
    setVoidDialogOpen(false);
    setVoidTarget(null);
    setVoidReason('');
  };

  const handleDelete = () => {
    if (!deleteTarget?.id) return;
    setDeletingId(deleteTarget.id);
    deleteInvoice(deleteTarget.id)
      .then(() => {
        message.success('Draft invoice deleted.');
        closeDeleteDialog();
        fetchInvoices();
      })
      .catch((err) => {
        console.error('Failed to delete invoice', err);
        message.error('Unable to delete this invoice. Please try again.');
      })
      .finally(() => setDeletingId(null));
  };

  const handleVoid = () => {
    if (!voidTarget?.id) return;
    setVoidingId(voidTarget.id);
    voidInvoice(voidTarget.id, { reason: voidReason })
      .then(() => {
        message.success('Invoice voided.');
        closeVoidDialog();
        fetchInvoices();
      })
      .catch((err) => {
        console.error('Failed to void invoice', err);
        message.error(err?.response?.data?.detail || 'Unable to void this invoice. Please try again.');
      })
      .finally(() => setVoidingId(null));
  };

  const handleBulkSend = () => {
    if (!selectedInvoiceIds.length) return;
    setBulkSending(true);
    bulkSendInvoices({ invoice_ids: selectedInvoiceIds })
      .then((res) => {
        const { sent = [], skipped = [] } = res.data || {};
        const skippedCount = skipped.length;
        if (sent.length) {
          message.success(`${sent.length} invoice${sent.length === 1 ? '' : 's'} sent.${skippedCount ? ` ${skippedCount} skipped.` : ''}`);
        } else {
          message.error('No invoices were sent. All selected invoices were ineligible.');
        }
        setSelectedInvoiceIds([]);
        fetchInvoices();
        fetchPeriodSummary();
      })
      .catch((err) => {
        console.error('Bulk send failed', err);
        message.error('Bulk send failed. Please try again.');
      })
      .finally(() => setBulkSending(false));
  };

  const handleBulkVoid = () => {
    if (!selectedInvoiceIds.length) return;
    setBulkVoiding(true);
    bulkVoidInvoices({ invoice_ids: selectedInvoiceIds, reason: bulkVoidReason })
      .then((res) => {
        const { voided = [], skipped = [] } = res.data || {};
        const skippedCount = skipped.length;
        if (voided.length) {
          message.success(`${voided.length} invoice${voided.length === 1 ? '' : 's'} voided.${skippedCount ? ` ${skippedCount} skipped.` : ''}`);
        } else {
          message.error('No invoices were voided. All selected invoices were ineligible.');
        }
        setSelectedInvoiceIds([]);
        setBulkVoidDialogOpen(false);
        setBulkVoidReason('');
        fetchInvoices();
        fetchPeriodSummary();
      })
      .catch((err) => {
        console.error('Bulk void failed', err);
        message.error('Bulk void failed. Please try again.');
      })
      .finally(() => setBulkVoiding(false));
  };

  const openBulkDialog = () => {
    setBulkSummary(null);
    setBulkError('');
    setBulkClients([]);
    setSelectedClientIds([]);
    setBulkPeriod({ start: '', end: '' });
    setBulkFundingFilter('all');
    setBulkDialogOpen(true);
  };

  const closeBulkDialog = () => {
    setBulkDialogOpen(false);
    setBulkClients([]);
  };

  const [exporting, setExporting] = useState(false);

  // Export every invoice matching the current filters, not just the page on
  // screen. Filtering to 106 invoices and exporting 25 of them without
  // saying so was worse than not offering the button.
  const handleExportCsv = async () => {
    let rowsToExport;
    if (selectedInvoiceIds.length) {
      rowsToExport = invoices.filter((invoice) => selectedInvoiceIdSet.has(String(invoice.id)));
    } else {
      setExporting(true);
      try {
        const res = await listInvoices({
          ...buildFilterQueryParams(),
          ordering: `${sort.direction === 'desc' ? '-' : ''}${sort.field}`,
          page: 1,
          page_size: 1000,
        });
        rowsToExport = mapInvoicesResponse(res.data).results;
      } catch (err) {
        console.error('Failed to export invoices', err);
        message.error('Unable to export invoices. Please try again.');
        setExporting(false);
        return;
      }
      setExporting(false);
    }

    if (!rowsToExport.length) {
      message.error('No invoices available to export for the current selection.');
      return;
    }

    const headers = [
      'Invoice', 'Client', 'Funding', 'Period', 'Status', 'Total', 'Balance due',
      'Revenue', 'Payment source', 'Payment method', 'Payment state',
      'Payment date', 'Issue date', 'Due date',
    ];

    const lines = [headers.map(toCsvCell).join(',')];
    rowsToExport.forEach((invoice) => {
      const row = [
        invoice.number || `Invoice #${invoice.id}`,
        invoice.client_name || invoice.client || '',
        formatSpendFundingLabel(clientFundingById.get(invoice.client)),
        getPeriodDisplayLabel(invoice.period_start, invoice.period_end),
        (invoice.status || '').toUpperCase(),
        toNumber(invoice.total_amount).toFixed(2),
        toNumber(invoice.balance_due).toFixed(2),
        toNumber(invoice.revenue_amount || 0).toFixed(2),
        formatPaymentSourceLabel(invoice.payment_source),
        formatPaymentMethodLabel(invoice.payment_method),
        getPaymentState(invoice).label,
        invoice.paid_at ? dayjs(invoice.paid_at).format('YYYY-MM-DD HH:mm') : '',
        invoice.issue_date || '',
        invoice.due_date || '',
      ];
      lines.push(row.map(toCsvCell).join(','));
    });

    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoices_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    message.success(`Exported ${rowsToExport.length} invoice${rowsToExport.length === 1 ? '' : 's'} to CSV.`);
  };

  const handleSelectAllBulkClients = () => {
    setSelectedClientIds(filteredClients.map((client) => client.id));
  };

  const handleClearBulkClients = () => {
    setSelectedClientIds([]);
  };

  const handleBulkSubmit = () => {
    if (!selectedClientIds.length) {
      setBulkError('Please choose at least one client.');
      return;
    }
    setBulkSubmitting(true);
    setBulkError('');
    setBulkSummary(null);
    bulkGenerateInvoices({
      client_ids: selectedClientIds,
      period_start: bulkPeriod.start || undefined,
      period_end: bulkPeriod.end || undefined,
    })
      .then((res) => {
        setBulkSummary(res.data);
        message.success('Invoices generated successfully.');
        fetchInvoices();
      })
      .catch((err) => {
        console.error('Failed to bulk generate invoices', err);
        setBulkError('Unable to generate invoices. Please review your selections.');
      })
      .finally(() => setBulkSubmitting(false));
  };

  const bulkSummaryDetails = useMemo(() => {
    if (!bulkSummary) return null;
    const created = bulkSummary.created || [];
    const skipped = bulkSummary.skipped_client_ids || [];
    const skippedExisting = bulkSummary.skipped_existing_client_ids || [];
    const skippedNoSpend = bulkSummary.skipped_no_spend_client_ids || [];
    const rolledIntoParent = bulkSummary.rolled_into_parent || [];
    const parentExisting = bulkSummary.skipped_parent_existing || [];
    const territoryWarnings = bulkSummary.territory_warnings || [];
    // Group the rolled-up brands by parent so the count reconciles: a run
    // that folds 44 brands into 4 group invoices otherwise looks like it
    // silently lost 44 invoices.
    const rolledByParent = rolledIntoParent.reduce((acc, row) => {
      const key = row.parent_name || `Parent ${row.parent_id}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return (
      <Stack spacing={1} mt={2}>
        <Alert severity="success">
          {created.length ? `${created.length} invoice(s) created.` : 'No new invoices were created.'}
        </Alert>
        {!!created.length && (
          <Paper variant="outlined">
            <Box p={2}>
              <Typography variant="subtitle2" gutterBottom>Created Invoices</Typography>
              <Stack spacing={0.5}>
                {created.map((invoice) => (
                  <Stack key={invoice.id} direction="row" spacing={1} alignItems="center">
                    <SummarizeIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {invoice.number || `Invoice #${invoice.id}`} — {invoice.client_name} ({invoice.period_start} → {invoice.period_end})
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Paper>
        )}
        {!!rolledIntoParent.length && (
          <Alert severity="info">
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {rolledIntoParent.length} brand{rolledIntoParent.length === 1 ? '' : 's'} rolled into{' '}
              {Object.keys(rolledByParent).length} group invoice
              {Object.keys(rolledByParent).length === 1 ? '' : 's'}
            </Typography>
            <Typography variant="body2">
              These companies are billed as one, so their brands do not get separate invoices:
            </Typography>
            <Stack component="ul" sx={{ pl: 2, my: 0.5 }} spacing={0.25}>
              {Object.entries(rolledByParent).map(([name, count]) => (
                <Typography component="li" variant="body2" key={name}>
                  {name} — {count} brand{count === 1 ? '' : 's'}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}
        {!!parentExisting.length && (
          <Alert severity="info">
            Group invoice already exists for this period:{' '}
            {parentExisting.map((row) => row.parent_name).join(', ')}
          </Alert>
        )}
        {!!territoryWarnings.length && (
          <Alert severity="warning">
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Grouped-client territory billing needs a look
            </Typography>
            <Stack component="ul" sx={{ pl: 2, my: 0.5 }} spacing={0.25}>
              {territoryWarnings.map((row, index) => (
                <Typography component="li" variant="body2" key={`${row.code}-${row.territory_id}-${index}`}>
                  {TERRITORY_WARNING_LABELS[row.code] || row.code}: {row.branch_client_name} — {row.territory} territory
                  on {row.grouped_client_name}, ${row.spend} ({row.period_start} → {row.period_end})
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}
        {!!skipped.length && <Alert severity="warning">Skipped client IDs: {skipped.join(', ')}</Alert>}
        {!!skippedExisting.length && <Alert severity="info">Existing invoices already covered client IDs: {skippedExisting.join(', ')}</Alert>}
        {!!skippedNoSpend.length && <Alert severity="warning">No spend in selected period for client IDs: {skippedNoSpend.join(', ')}</Alert>}
      </Stack>
    );
  }, [bulkSummary]);

  // Mark paid and Charge now are the two actions staff actually reach for:
  // 852 and 313 uses respectively over 180 days, against 24 for Record
  // payment. Both previously required opening the detail page.
  const [payTarget, setPayTarget] = useState(null);
  const [payMethod, setPayMethod] = useState('us_bank_account');
  const [payMode, setPayMode] = useState('mark_paid');
  const [paySubmitting, setPaySubmitting] = useState(false);

  const openPayDialog = (invoice, mode) => {
    setPayTarget(invoice);
    setPayMode(mode);
    setPayMethod(
      mode === 'charge'
        ? (invoice?.client_autopay_consent?.methods || [])[0] || 'us_bank_account'
        : invoice.payment_method || 'us_bank_account',
    );
  };

  const closePayDialog = () => {
    if (paySubmitting) return;
    setPayTarget(null);
  };

  const handlePaySubmit = () => {
    if (!payTarget?.id) return;
    setPaySubmitting(true);
    const request =
      payMode === 'charge'
        ? autoChargeInvoice(payTarget.id, { payment_method: payMethod })
        : markInvoicePaid(payTarget.id, { payment_method: payMethod });
    request
      .then((res) => {
        if (payMode === 'charge') {
          const result = res?.data?.result || res?.data || {};
          message.success(
            result.status === 'processing'
              ? `Charge is processing. ${payTarget.number} will be marked paid once it clears.`
              : `Charged ${formatCurrency(result.amount ?? payTarget.total_amount)} for ${payTarget.number}.`,
          );
        } else {
          message.success(`${payTarget.number} marked paid.`);
        }
        setPayTarget(null);
        fetchInvoices();
        fetchPeriodSummary();
      })
      .catch((err) => {
        console.error('Payment action failed', err);
        message.error(
          err?.response?.data?.detail ||
            err?.response?.data?.payment_method?.[0] ||
            'Unable to complete that action.',
        );
      })
      .finally(() => setPaySubmitting(false));
  };

  const canMarkPaid = (invoice) =>
    !['paid', 'void'].includes(String(invoice?.status || '').toLowerCase());

  const canChargeNow = (invoice) => {
    if (!canMarkPaid(invoice)) return false;
    const consent = invoice?.client_autopay_consent;
    return Boolean(consent?.authorized && (consent.methods || []).length);
  };

  const renderInvoiceRow = (invoice) => (
    <TableRow key={invoice.id} hover>
      <TableCell padding="checkbox">
        <Checkbox
          checked={selectedInvoiceIdSet.has(String(invoice.id))}
          onChange={() => handleToggleInvoiceSelection(invoice.id)}
          inputProps={{ 'aria-label': `select invoice ${invoice.id}` }}
        />
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          fontWeight={600}
          component={RouterLink}
          to={paths.dashboard.invoices.details(invoice.id)}
          sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          {invoice.number || `Invoice #${invoice.id}`}
        </Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
          <Typography variant="body2">{invoice.client_name || invoice.client}</Typography>
          <AutopayBadge consent={invoice.client_autopay_consent} />
        </Stack>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {getPeriodDisplayLabel(invoice.period_start, invoice.period_end)}
        </Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
          <Chip label={getStatusPresentation(invoice).label} color={getStatusPresentation(invoice).color} size="small" />
          {invoice.payment_status === 'partial' && (
            <Chip label={`Partial (${formatCurrency(invoice.amount_paid)})`} color="warning" size="small" variant="outlined" />
          )}
          {invoice.payment_report?.active && (
            <Tooltip
              title={
                `Client reports payment sent${
                  invoice.payment_report.sent_on ? ` ${invoice.payment_report.sent_on}` : ''
                }` +
                `${invoice.payment_report.reference ? ` · ref ${invoice.payment_report.reference}` : ''}` +
                ` · reminders held until ${invoice.payment_report.expires_on}`
              }
            >
              <Chip
                label={`Payment reported${
                  invoice.payment_report.reference ? ` · ${invoice.payment_report.reference}` : ''
                }`}
                color="info"
                size="small"
                variant="outlined"
              />
            </Tooltip>
          )}
          {invoice.late_strike && (
            <Tooltip
              title={
                invoice.late_strike.waived
                  ? `Strike waived${invoice.late_strike.waive_reason ? `: ${invoice.late_strike.waive_reason}` : ''}`
                  : `Late-payment strike — hit day-6 pause ${dayjs(invoice.late_strike.struck_at).format('MMM D, YYYY')}`
              }
            >
              <Chip
                label={invoice.late_strike.waived ? 'Strike waived' : 'Strike'}
                color={invoice.late_strike.waived ? 'default' : 'error'}
                size="small"
                variant="outlined"
              />
            </Tooltip>
          )}
        </Stack>
      </TableCell>
      <TableCell>{formatCurrency(invoice.total_amount)}</TableCell>
      <TableCell align="right">
        {/* balance_due is already served by the API and was going unused. */}
        <Typography
          variant="body2"
          fontWeight={toNumber(invoice.balance_due) > 0 ? 600 : 400}
          color={toNumber(invoice.balance_due) > 0 ? 'warning.dark' : 'text.secondary'}
        >
          {formatCurrency(invoice.balance_due)}
        </Typography>
      </TableCell>
      <TableCell>{invoice.due_date || '—'}</TableCell>
      <TableCell>
        <Chip label={getPaymentState(invoice).label} color={getPaymentState(invoice).color} size="small" />
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
          <Tooltip title="View details">
            <IconButton
              component={RouterLink}
              to={paths.dashboard.invoices.details(invoice.id)}
              color="primary"
              size="small"
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              invoice.status === 'paid' || invoice.status === 'void'
                ? 'Invoice cannot be sent'
                : getPaymentState(invoice).label === 'Processing'
                  ? 'Payment is processing'
                  : invoice.needs_amount_confirmation
                    ? 'Approve and send'
                    : 'Quick send'
            }
          >
            <span>
              <IconButton
                color="primary"
                size="small"
                onClick={() => openQuickSend(invoice)}
                disabled={!canSendInvoice(invoice)}
                aria-label={`quick send invoice ${invoice.id}`}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canMarkPaid(invoice) ? 'Mark paid' : 'Already settled'}>
            <span>
              <IconButton
                color="success"
                size="small"
                onClick={() => openPayDialog(invoice, 'mark_paid')}
                disabled={!canMarkPaid(invoice)}
                aria-label={`mark invoice ${invoice.id} paid`}
              >
                <PriceCheckIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              canChargeNow(invoice)
                ? 'Charge saved payment method'
                : 'Client has not authorized auto-charge'
            }
          >
            <span>
              <IconButton
                color="primary"
                size="small"
                onClick={() => openPayDialog(invoice, 'charge')}
                disabled={!canChargeNow(invoice)}
                aria-label={`charge invoice ${invoice.id}`}
              >
                <BoltIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <InvoiceActionMenu
            invoice={invoice}
            onDownloadPdf={handleDownloadPdf}
            onVoid={openVoidDialog}
            onDelete={openDeleteDialog}
            downloadingId={downloadingId}
            voidingId={voidingId}
            deletingId={deletingId}
          />
        </Stack>
      </TableCell>
    </TableRow>
  );

  return (
    <Box p={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} mb={3}>
        <Typography variant="h4">Invoices</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} width={{ xs: '100%', sm: 'auto' }}>
          <LoadingButton
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportCsv}
            loading={exporting}
            disabled={!invoices.length}
          >
            {selectedInvoiceIds.length ? `Export ${selectedInvoiceIds.length}` : 'Export all matching'}
          </LoadingButton>
          <Button variant="outlined" startIcon={<SummarizeIcon />} onClick={openBulkDialog}>
            Bulk generate
          </Button>
          <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => navigate(paths.dashboard.invoices.settings)}>
            Invoice settings
          </Button>
          <Button variant="contained" startIcon={<AddCircleOutlineIcon />} color="primary" onClick={() => navigate(paths.dashboard.invoices.create)}>
            New invoice
          </Button>
        </Stack>
      </Stack>

      <FacebookCardsStaffPanel />

      {/* --- Filters --- */}
      <Card>
        <CardHeader
          title="Filters"
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                startIcon={<FilterListIcon />}
                onClick={() => setShowMoreFilters((prev) => !prev)}
              >
                {showMoreFilters ? 'Less filters' : 'More filters'}
                {activeAdvancedFilterCount > 0 && !showMoreFilters && (
                  <Chip size="small" label={activeAdvancedFilterCount} color="primary" sx={{ ml: 0.5 }} />
                )}
              </Button>
              <IconButton onClick={() => { fetchInvoices(); fetchPeriodSummary(); }} aria-label="Refresh">
                <RefreshIcon />
              </IconButton>
            </Stack>
          }
        />
        <Divider />
        <CardContent>
          <Stack spacing={2}>
            {/* Primary filters - always visible */}
            <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
              <TextField
                label="Search"
                placeholder="Invoice number or client"
                value={filters.search}
                onChange={handleFilterChange('search')}
                fullWidth
                sx={{ minWidth: 240, flex: 1 }}
              />
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={filters.status} onChange={handleFilterChange('status')}>
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Advanced filters - collapsible */}
            <Collapse in={showMoreFilters}>
              <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
                <TextField
                  label="Amount"
                  placeholder="Approx total (±$3)"
                  value={filters.amount}
                  onChange={handleFilterChange('amount')}
                  sx={{ minWidth: 160 }}
                />
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel>Funding</InputLabel>
                  <Select label="Funding" value={filters.funding} onChange={handleFilterChange('funding')}>
                    <MenuItem value="all">All funding</MenuItem>
                    <MenuItem value="guru_funds_spend">Lead Gurus funded</MenuItem>
                    <MenuItem value="client_funds_spend">Client funded</MenuItem>
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 170 }}>
                  <InputLabel>Approval</InputLabel>
                  <Select label="Approval" value={filters.approval} onChange={handleFilterChange('approval')}>
                    <MenuItem value="all">All approvals</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="needs_approval">Needs approval</MenuItem>
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 190 }}>
                  <InputLabel>Payment method</InputLabel>
                  <Select label="Payment method" value={filters.paymentMethod} onChange={handleFilterChange('paymentMethod')}>
                    {PAYMENT_METHOD_FILTER_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 190 }}>
                  <InputLabel>Payment state</InputLabel>
                  <Select label="Payment state" value={filters.paymentState} onChange={handleFilterChange('paymentState')}>
                    {PAYMENT_STATE_FILTER_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField type="date" label="Payment date from" value={filters.paymentDateStart} onChange={handleFilterChange('paymentDateStart')} InputLabelProps={{ shrink: true }} sx={{ minWidth: 170 }} />
                <TextField type="date" label="Payment date to" value={filters.paymentDateEnd} onChange={handleFilterChange('paymentDateEnd')} InputLabelProps={{ shrink: true }} sx={{ minWidth: 170 }} />
                <TextField type="date" label="Issued after" value={filters.issueStart} onChange={handleFilterChange('issueStart')} InputLabelProps={{ shrink: true }} sx={{ minWidth: 170 }} />
                <TextField type="date" label="Issued before" value={filters.issueEnd} onChange={handleFilterChange('issueEnd')} InputLabelProps={{ shrink: true }} sx={{ minWidth: 170 }} />
              </Stack>
            </Collapse>
          </Stack>
        </CardContent>
      </Card>

      {/* --- Summary cards --- */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={3}>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="overline" color="text.secondary">Total Billed</Typography>
            <Typography variant="h5">{formatCurrency(totals.total_billed)}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="overline" color="success.main">Collected</Typography>
            <Typography variant="h5" color="success.main">{formatCurrency(totals.total_paid)}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="overline" color="warning.main">Outstanding</Typography>
            <Typography variant="h5" color="warning.main">{formatCurrency(totals.total_outstanding)}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="overline" color="error.main">Overdue</Typography>
            <Typography variant="h5" color="error.main">{formatCurrency(totals.total_overdue)}</Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* --- Late-payment standings --- */}
      <LatePaymentStandingsPanel />

      {/* --- Chart --- */}
      <Box mt={3}>
        <Card>
          <CardHeader
            title="Billing Period Performance"
            subheader={
              periodSummaryLoading
                ? 'Loading...'
                : `${filteredPeriodSummaries.length} period${filteredPeriodSummaries.length === 1 ? '' : 's'}`
            }
            action={
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Window</InputLabel>
                <Select label="Window" value={periodWindow} onChange={(event) => setPeriodWindow(event.target.value)}>
                  {PERIOD_WINDOW_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            }
          />
          <Divider />
          <CardContent>
            {chartData.length ? (
              <Box sx={{ width: '100%', height: 320 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis tickFormatter={(value) => `$${Number(value).toLocaleString()}`} />
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} labelFormatter={(label) => `Period: ${label}`} />
                    <Legend />
                    <Bar dataKey="paid" name="Paid" fill="#2e7d32" stackId="a" />
                    <Bar dataKey="outstanding" name="Outstanding" fill="#ed6c02" stackId="a" />
                    <Bar dataKey="overdue" name="Overdue" fill="#d32f2f" stackId="a" />
                    <Bar dataKey="draft" name="Draft" fill="#9e9e9e" stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Typography color="text.secondary">No period data available for current filters.</Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* --- Invoice table --- */}
      <Box mt={3}>
        <Card>
          <CardHeader
            title="Invoice list"
            subheader={
              selectedInvoiceIds.length
                ? `${selectedInvoiceIds.length} selected`
                : 'Select rows to export specific invoices'
            }
            action={
              selectedInvoiceIds.length > 0 && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <LoadingButton
                    size="small"
                    variant="outlined"
                    color="secondary"
                    startIcon={<SendIcon />}
                    loading={bulkSending}
                    onClick={handleBulkSend}
                  >
                    Send ({selectedInvoiceIds.length})
                  </LoadingButton>
                  <LoadingButton
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<BlockIcon />}
                    loading={bulkVoiding}
                    onClick={() => { setBulkVoidReason(''); setBulkVoidDialogOpen(true); }}
                  >
                    Void ({selectedInvoiceIds.length})
                  </LoadingButton>
                </Stack>
              )
            }
          />
          <Divider />
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mx: 3, mt: 3 }}>{error}</Alert>
              )}
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={someVisibleSelected}
                          checked={allVisibleSelected}
                          onChange={handleToggleSelectVisible}
                          inputProps={{ 'aria-label': 'select all visible invoices' }}
                        />
                      </TableCell>
                      <TableCell>Invoice</TableCell>
                      <TableCell sortDirection={sort.field === 'client_sort_name' ? sort.direction : false}>
                        <TableSortLabel
                          active={sort.field === 'client_sort_name'}
                          direction={sort.field === 'client_sort_name' ? sort.direction : 'asc'}
                          onClick={() => handleRequestSort('client_sort_name')}
                        >
                          Client
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell sortDirection={sort.field === 'total_amount' ? sort.direction : false}>
                        <TableSortLabel
                          active={sort.field === 'total_amount'}
                          direction={sort.field === 'total_amount' ? sort.direction : 'asc'}
                          onClick={() => handleRequestSort('total_amount')}
                        >
                          Total
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">Balance due</TableCell>
                      <TableCell sortDirection={sort.field === 'due_date' ? sort.direction : false}>
                        <TableSortLabel
                          active={sort.field === 'due_date'}
                          direction={sort.field === 'due_date' ? sort.direction : 'asc'}
                          onClick={() => handleRequestSort('due_date')}
                        >
                          Due date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Payment</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                          <Typography color="text.secondary">No invoices found for the selected filters.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      invoicePeriodGroups.map((group) => {
                        const groupSummary = periodSummaryByKey.get(group.key);
                        return (
                          <Fragment key={group.key}>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                              <TableCell colSpan={10}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <IconButton
                                    size="small"
                                    onClick={() => togglePeriodCollapse(group.key)}
                                    aria-label={collapsedPeriods[group.key] ? 'Expand' : 'Collapse'}
                                  >
                                    {collapsedPeriods[group.key] ? (
                                      <KeyboardArrowDownIcon fontSize="small" />
                                    ) : (
                                      <KeyboardArrowUpIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                  <Typography variant="subtitle2" fontWeight={700}>
                                    {getPeriodDisplayLabel(group.periodStart, group.periodEnd)}
                                  </Typography>
                                  <Chip size="small" label={`${group.invoices.length} invoice${group.invoices.length === 1 ? '' : 's'}`} variant="outlined" />
                                  {groupSummary && (
                                    <>
                                      <Chip size="small" variant="outlined" color="success" label={`Paid: ${formatCurrency(groupSummary.total_paid)}`} />
                                      <Chip size="small" variant="outlined" color="warning" label={`Outstanding: ${formatCurrency(groupSummary.total_outstanding)}`} />
                                      {groupSummary.total_overdue > 0 && (
                                        <Chip size="small" variant="outlined" color="error" label={`Overdue: ${formatCurrency(groupSummary.total_overdue)}`} />
                                      )}
                                    </>
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                            {!collapsedPeriods[group.key] && group.invoices.map((invoice) => renderInvoiceRow(invoice))}
                          </Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalCount}
                page={pagination.page}
                onPageChange={handlePageChange}
                rowsPerPage={pagination.rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </Card>
      </Box>

      {/* --- Bulk generate dialog --- */}
      <Dialog open={bulkDialogOpen} onClose={closeBulkDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk generate invoices</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              select
              label="Funding segment"
              value={bulkFundingFilter}
              onChange={(event) => setBulkFundingFilter(event.target.value)}
              helperText="Filter clients by ad spend funding."
            >
              <MenuItem value="all">All clients</MenuItem>
              <MenuItem value="guru_funds_spend">Lead Gurus funded ad spend</MenuItem>
              <MenuItem value="client_funds_spend">Client funded ad spend</MenuItem>
            </TextField>
            <TextField
              select
              label="Clients"
              value={selectedClientIds}
              onChange={(event) => setSelectedClientIds(event.target.value.map((value) => Number(value)))}
              SelectProps={{ multiple: true, renderValue: (selected) => `${selected.length} selected` }}
              helperText="Only clients with spend in the selected period are shown."
            >
              {bulkClientsLoading ? (
                <MenuItem disabled>Loading clients…</MenuItem>
              ) : !filteredClients.length ? (
                <MenuItem disabled>No clients with spend found for this period.</MenuItem>
              ) : (
                filteredClients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip size="small" label={client.id} />
                      <Typography variant="body2">
                        {client.name} ({formatCurrency(client.periodSpend)})
                      </Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${getPeriodDisplayLabel(client.periodStart, client.periodEnd)}`}
                      />
                    </Stack>
                  </MenuItem>
                ))
              )}
            </TextField>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={handleClearBulkClients}>Clear</Button>
              <Button size="small" variant="outlined" onClick={handleSelectAllBulkClients} disabled={!filteredClients.length}>Select all</Button>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField type="date" label="Period start" value={bulkPeriod.start} onChange={(event) => setBulkPeriod((prev) => ({ ...prev, start: event.target.value }))} InputLabelProps={{ shrink: true }} fullWidth />
              <TextField type="date" label="Period end" value={bulkPeriod.end} onChange={(event) => setBulkPeriod((prev) => ({ ...prev, end: event.target.value }))} InputLabelProps={{ shrink: true }} fullWidth />
            </Stack>
            <Alert severity="info">
              Leave the period empty to auto-detect each client's billing period based on their billing cycle
              (P1/P2 for semi-monthly, full month for monthly clients).
            </Alert>
            {bulkError && <Alert severity="error">{bulkError}</Alert>}
            {bulkSummaryDetails}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulkDialog}>Cancel</Button>
          <LoadingButton variant="contained" loading={bulkSubmitting} onClick={handleBulkSubmit} startIcon={<SummarizeIcon />}>
            Generate
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* --- Delete dialog --- */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Delete draft invoice?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography variant="body2">
              This will permanently delete <strong>{deleteTarget?.number || (deleteTarget ? `Invoice #${deleteTarget.id}` : 'this draft')}</strong>.
            </Typography>
            <Alert severity="warning">Only draft invoices can be deleted.</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={Boolean(deletingId)}>Cancel</Button>
          <LoadingButton color="error" variant="contained" loading={Boolean(deletingId)} onClick={handleDelete}>Delete</LoadingButton>
        </DialogActions>
      </Dialog>

      {/* --- Void dialog --- */}
      <Dialog open={Boolean(payTarget)} onClose={closePayDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {payMode === 'charge' ? 'Charge saved payment method' : 'Mark invoice paid'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2">
              {payTarget?.number} — {formatCurrency(payTarget?.balance_due ?? payTarget?.total_amount)}
              {payTarget?.client_name ? ` · ${payTarget.client_name}` : ''}
            </Typography>
            {payMode === 'charge' ? (
              <Alert severity="warning">
                This charges the client&apos;s saved payment method immediately.
              </Alert>
            ) : (
              <Alert severity="info">
                Records that payment arrived outside the app. No charge is made.
              </Alert>
            )}
            <FormControl fullWidth size="small">
              <InputLabel>Payment method</InputLabel>
              <Select
                label="Payment method"
                value={payMethod}
                onChange={(event) => setPayMethod(event.target.value)}
              >
                {(payMode === 'charge'
                  ? payTarget?.client_autopay_consent?.methods || []
                  : ['us_bank_account', 'ach_wire', 'check', 'card']
                ).map((method) => (
                  <MenuItem key={method} value={method}>
                    {formatPaymentMethodLabel(method)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePayDialog} disabled={paySubmitting}>Cancel</Button>
          <LoadingButton
            variant="contained"
            color={payMode === 'charge' ? 'warning' : 'success'}
            loading={paySubmitting}
            onClick={handlePaySubmit}
          >
            {payMode === 'charge' ? 'Charge now' : 'Mark paid'}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={voidDialogOpen} onClose={closeVoidDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Void invoice?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning">Voiding keeps the invoice for history and reporting, but removes it from active billing workflows.</Alert>
            <Typography variant="body2">
              You are voiding <strong>{voidTarget?.number || (voidTarget ? `Invoice #${voidTarget.id}` : 'this invoice')}</strong>.
            </Typography>
            <TextField label="Reason" value={voidReason} onChange={(event) => setVoidReason(event.target.value)} multiline minRows={3} placeholder="Optional reason for voiding this invoice" fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeVoidDialog} disabled={Boolean(voidingId)}>Cancel</Button>
          <LoadingButton color="warning" onClick={handleVoid} loading={Boolean(voidingId)}>Void invoice</LoadingButton>
        </DialogActions>
      </Dialog>

      {/* --- Quick send dialog --- */}
      <QuickSendDialog
        open={Boolean(quickSendTarget)}
        invoice={quickSendTarget}
        onClose={closeQuickSend}
        onSuccess={handleQuickSendSuccess}
      />

      {/* --- Bulk void dialog --- */}
      <Dialog open={bulkVoidDialogOpen} onClose={() => { if (!bulkVoiding) setBulkVoidDialogOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Void {selectedInvoiceIds.length} invoice{selectedInvoiceIds.length === 1 ? '' : 's'}?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning">
              This will void all eligible selected invoices. Paid, void, and processing invoices will be skipped.
            </Alert>
            <TextField
              label="Reason"
              value={bulkVoidReason}
              onChange={(event) => setBulkVoidReason(event.target.value)}
              multiline
              minRows={2}
              placeholder="Optional reason for voiding these invoices"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkVoidDialogOpen(false)} disabled={bulkVoiding}>Cancel</Button>
          <LoadingButton color="warning" onClick={handleBulkVoid} loading={bulkVoiding}>
            Void {selectedInvoiceIds.length} invoice{selectedInvoiceIds.length === 1 ? '' : 's'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
